export type Market = 'INT' | 'MX' | 'BR';
export type PaymentFrequency = 'M' | 'Q' | 'S' | 'A';
export type FlywireRecurringType = 'tokenization' | 'optional_tokenization';

export const TOKENIZATION: FlywireRecurringType = 'tokenization';
export const OPTIONAL_TOKENIZATION: FlywireRecurringType = 'optional_tokenization';

const ANNUALIZATION_FACTOR: Record<PaymentFrequency, number> = {
  A: 1,
  S: 2,
  Q: 4,
  M: 12,
};

const SEMI_ANNUAL_AND_ANNUAL: readonly string[] = ['S', 'A'];

export const isSemiAnnualOrAnnual = (frequency: string): boolean =>
  SEMI_ANNUAL_AND_ANNUAL.includes(frequency);

export const getAnnualizedPremium = (amount: number, frequency: string): number => {
  const factor = ANNUALIZATION_FACTOR[frequency as PaymentFrequency];
  return factor ? amount * factor : amount;
};

interface OmitPaymentParams {
  market: string;
  country: string;
  annualizedPremium: number;
  frequency: string;
  automaticApproval: boolean;
  omitThreshold: number;
  countryException: string;
}

const canOmitByLegacyRule = (frequency: string, automaticApproval: boolean): boolean =>
  !automaticApproval && isSemiAnnualOrAnnual(frequency);

const matchesCountryException = (country: string, countryException: string): boolean =>
  country.toUpperCase() === countryException.toUpperCase();

export const canOmitPayment = ({
  market,
  country,
  annualizedPremium,
  frequency,
  automaticApproval,
  omitThreshold,
  countryException,
}: OmitPaymentParams): boolean => {
  if (market === 'BR') {
    return false;
  }

  if (market !== 'INT') {
    return canOmitByLegacyRule(frequency, automaticApproval);
  }

  if (matchesCountryException(country, countryException)) {
    return isSemiAnnualOrAnnual(frequency);
  }

  return annualizedPremium >= omitThreshold || isSemiAnnualOrAnnual(frequency);
};

/**
 * ¿Flywire cobra en este momento, o solo se registra la tarjeta?
 * Única fuente de verdad: la consume tanto transformObjectResponse (para el monto)
 * como resolveRecurrence (para el recurringType), de modo que no puedan desincronizarse.
 * BR nunca cobra en el checkout: la primera cuota se cobra al aprobar la solicitud.
 */
export const isChargedUpfront = (market: string, automaticApproval: boolean): boolean =>
  market === 'BR' ? false : automaticApproval;

interface RecurrenceParams {
  market: string;
  country: string;
  frequency: string;
  automaticApproval: boolean;
  userAuthorized: boolean;
  countryException: string;
  isFirstPaymentScreen?: boolean;
}

interface RecurrenceDecision {
  mandatory: boolean;
  recurrent: boolean;
  chargeNow: boolean;
  flywireRecurringType: FlywireRecurringType;
}

const legacyFlywireRecurringType = (
  automaticApproval: boolean,
  userAuthorized: boolean,
): FlywireRecurringType =>
  automaticApproval && !userAuthorized ? OPTIONAL_TOKENIZATION : TOKENIZATION;

export const resolveRecurrence = ({
  market,
  country,
  frequency,
  automaticApproval,
  userAuthorized,
  countryException,
  isFirstPaymentScreen = false,
}: RecurrenceParams): RecurrenceDecision => {
  const chargeNow = isChargedUpfront(market, automaticApproval);

  if (market === 'INT') {
    const mandatory =
      matchesCountryException(country, countryException)
        ? !isSemiAnnualOrAnnual(frequency)
        : automaticApproval || !isSemiAnnualOrAnnual(frequency);

    return {
      mandatory,
      recurrent: mandatory || userAuthorized,
      chargeNow,
      // Sin cobro no hay nada que tokenizar "opcionalmente": Flywire rechaza
      // optional_tokenization con amount 0, y además el token es imprescindible
      // para poder cobrar cuando se apruebe la solicitud.
      flywireRecurringType: !chargeNow || mandatory ? TOKENIZATION : OPTIONAL_TOKENIZATION,
    };
  }

  const mandatory = !isSemiAnnualOrAnnual(frequency);
  const forcedByFirstPaymentInBrazil = market === 'BR' && isFirstPaymentScreen;

  return {
    mandatory,
    recurrent: mandatory || userAuthorized,
    chargeNow,
    flywireRecurringType: !chargeNow || forcedByFirstPaymentInBrazil
      ? TOKENIZATION
      : legacyFlywireRecurringType(automaticApproval, userAuthorized),
  };
};
